import React from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import AnimatedSection from '../components/common/AnimatedSection';
import ContactForm from '../components/common/ContactForm';
import SEOHead from '../components/common/SEOHead';
import { companyInfo } from '../data/mock';

const ContactPage = () => {
  return (
    <>
      <SEOHead 
        title="Contato"
        description={`Entre em contato com a ${companyInfo.name}. Telefone: ${companyInfo.phone}, WhatsApp: ${companyInfo.whatsappFormatted}, Email: ${companyInfo.email}`}
        keywords="contato Paulare, telefone Paulare, whatsapp Paulare, orçamento empilhadeira"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900/30 py-20">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Entre em Contato</h1>
            <p className="text-xl text-gray-400">Estamos prontos para atender você</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <AnimatedSection animation="fadeRight">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Fale Conosco</h2>
              
              <div className="space-y-6 mb-12">
                {/* Phone */}
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-100 hover:border-teal-200 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Telefone</h3>
                    <p className="text-lg text-teal-600 font-semibold">{companyInfo.phone}</p>
                  </div>
                </a>

                {/* WhatsApp */}
                <a 
                  href={`https://wa.me/${companyInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-100 hover:border-green-200 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">WhatsApp</h3>
                    <p className="text-lg text-green-600 font-semibold">{companyInfo.whatsappFormatted}</p>
                    <p className="text-sm text-gray-500">Clique para conversar</p>
                  </div>
                </a>

                {/* Email */}
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-100 hover:border-teal-200 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">E-mail</h3>
                    <p className="text-lg text-teal-600 font-semibold">{companyInfo.email}</p>
                  </div>
                </a>

                {/* Address */}
                <div className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Endereço</h3>
                    <p className="text-gray-600">
                      {companyInfo.address.street}<br />
                      {companyInfo.address.neighborhood}<br />
                      {companyInfo.address.city}/{companyInfo.address.state}<br />
                      CEP: {companyInfo.address.cep}
                    </p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Horário de Atendimento</h3>
                    <p className="text-gray-600">
                      Segunda a Sexta<br />
                      08:00 - 18:00
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Contact Form */}
            <AnimatedSection animation="fadeLeft">
              <ContactForm 
                title="Envie sua Mensagem"
                subtitle="Preencha o formulário e retornaremos em até 24 horas."
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-white">
        <div className="container mx-auto px-4 py-12">
          <AnimatedSection animation="fadeUp" className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Nossa Localização</h2>
            <p className="text-gray-600 mt-2">Venha nos visitar!</p>
          </AnimatedSection>
        </div>
        <div className="h-96 bg-gray-200">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.8!2d-47.6778!3d-22.9064!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDU0JzIzLjAiUyA0N8KwNDAnNDAuMSJX!5e0!3m2!1spt-BR!2sbr!4v1234567890!5m2!1spt-BR!2sbr"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Localização Paulare Empilhadeiras"
          ></iframe>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
